import * as THREE from 'three';
import { commonGLSL, curvatureGLSL, lightingGLSL } from '../shaders/common';
import { shadowGLSL, type ShadowCaster } from '../render/Shadows';
import { shared } from '../render/SharedUniforms';
import { strataGLSL } from './Strata';
import { groundGLSL, type GroundSet } from './GroundTextures';
import type { TerrainData } from './TerrainData';

const LEAF = 64;
const MAX_LEVEL = 13;
const MAX_NODES = 3000;

export const terrainHeightGLSL = /* glsl */ `
uniform sampler2D uH0, uH1, uH2;
uniform vec4 uHB0, uHB1, uHB2; // x0, z0, size, res
uniform vec3 uHM;             // blend margins near, mid
float sampleH(sampler2D t, vec4 b, vec2 p) {
  vec2 f = (p - b.xy) / b.z * b.w - 0.5;
  f = clamp(f, vec2(0.0), vec2(b.w - 1.001));
  ivec2 i = ivec2(floor(f));
  vec2 t2 = f - vec2(i);
  float h00 = texelFetch(t, i, 0).r;
  float h10 = texelFetch(t, i + ivec2(1, 0), 0).r;
  float h01 = texelFetch(t, i + ivec2(0, 1), 0).r;
  float h11 = texelFetch(t, i + ivec2(1, 1), 0).r;
  return mix(mix(h00, h10, t2.x), mix(h01, h11, t2.x), t2.y);
}
float edgeW(vec4 b, float margin, vec2 p) {
  vec2 d = min(p - b.xy, b.xy + b.z - p);
  float w = clamp(min(d.x, d.y) / margin, 0.0, 1.0);
  return w * w * (3.0 - 2.0 * w);
}
float terrainHeight(vec2 p) {
  float h = sampleH(uH2, uHB2, p);
  float w1 = edgeW(uHB1, uHM.y, p);
  if (w1 > 0.0) h = mix(h, sampleH(uH1, uHB1, p), w1);
  float w0 = edgeW(uHB0, uHM.x, p);
  if (w0 > 0.0) h = mix(h, sampleH(uH0, uHB0, p), w0);
  return h;
}
`;

const terrainVert = /* glsl */ `
in vec4 aNode; // x0, z0, size, level
uniform vec2 uMorph[${MAX_LEVEL + 1}];
uniform float uGridN;
uniform vec3 uLodCam;
uniform float uLodScale;
out vec3 vWorld;
out float vViewDist;
void main() {
  vec2 g = position.xz * uGridN;
  vec2 wp = aNode.xy + position.xz * aNode.z;
  float h = terrainHeight(wp);
  float dist = distance(vec3(wp.x, h, wp.y), uLodCam) * uLodScale;
  int lvl = int(aNode.w + 0.5);
  vec2 m = uMorph[lvl];
  float k = clamp((dist - m.x) * m.y, 0.0, 1.0);
  vec2 fracPart = fract(g * 0.5) * 2.0;
  g -= fracPart * k;
  wp = aNode.xy + g / uGridN * aNode.z;
  h = terrainHeight(wp);
  vec3 world = vec3(wp.x, h, wp.y);
  vWorld = world;
  world.y -= curvatureDrop(world);
  vec4 mv = viewMatrix * vec4(world, 1.0);
  vViewDist = -mv.z;
  gl_Position = projectionMatrix * mv;
}
`;

const terrainFrag = /* glsl */ `
uniform sampler2D uN0, uN1, uN2; // rg normal xz, b sky visibility, a vegetation
uniform sampler2D uC0, uC1, uC2;
uniform sampler2D uTrail;
uniform sampler2D uNoise;
uniform float uDetailFade;
in vec3 vWorld;
in float vViewDist;
layout(location = 0) out vec4 fragColor;

vec2 uvOf(vec4 b, vec2 p) { return (p - b.xy) / b.z; }

vec3 triNoiseNormal(vec3 wp, vec3 N, float scale, float amp) {
  // cheap triplanar bump from the tileable noise texture
  vec3 w = pow(abs(N), vec3(4.0));
  w /= (w.x + w.y + w.z);
  float e = 0.08;
  vec3 g = vec3(0.0);
  if (w.y > 0.02) {
    vec2 uv = wp.xz * scale;
    float c = texture(uNoise, uv).r;
    g += w.y * vec3(texture(uNoise, uv + vec2(e * scale, 0.0)).r - c, 0.0, texture(uNoise, uv + vec2(0.0, e * scale)).r - c);
  }
  if (w.x > 0.02) {
    vec2 uv = vec2(wp.z * scale, wp.y * scale * 2.5);
    float c = texture(uNoise, uv).r;
    g += w.x * vec3(0.0, texture(uNoise, uv + vec2(0.0, e * scale)).r - c, texture(uNoise, uv + vec2(e * scale, 0.0)).r - c);
  }
  if (w.z > 0.02) {
    vec2 uv = vec2(wp.x * scale, wp.y * scale * 2.5);
    float c = texture(uNoise, uv).r;
    g += w.z * vec3(texture(uNoise, uv + vec2(e * scale, 0.0)).r - c, texture(uNoise, uv + vec2(0.0, e * scale)).r - c, 0.0);
  }
  return normalize(N - g * amp / e);
}

void main() {
  vec2 p = vWorld.xz;
  float w0 = edgeW(uHB0, uHM.x, p);
  float w1 = edgeW(uHB1, uHM.y, p);
  vec2 uv0 = uvOf(uHB0, p), uv1 = uvOf(uHB1, p), uv2 = uvOf(uHB2, p);

  vec4 nt = texture(uN2, uv2);
  vec3 alb = texture(uC2, uv2).rgb;
  if (w1 > 0.0) {
    nt = mix(nt, texture(uN1, uv1), w1);
    alb = mix(alb, texture(uC1, uv1).rgb, w1);
  }
  if (w0 > 0.0) {
    nt = mix(nt, texture(uN0, uv0), w0);
    alb = mix(alb, texture(uC0, uv0).rgb, w0);
  }
  vec2 nn = nt.rg * 2.0 - 1.0;
  vec3 N = normalize(vec3(nn.x, sqrt(max(1.0 - dot(nn, nn), 0.0004)), nn.y));
  float skyVis = nt.b;
  float veg = nt.a;
  float dist = vViewDist;

  // --- imagery is desaturated: restore some chroma
  float la = lum3(alb);
  alb = max(vec3(0.0), la + (alb - la) * 1.35);

  // --- Claron Formation: color comes from the strata palette, imagery only modulates brightness
  float steep = 1.0 - N.y;
  float rockMask = smoothstep(0.28, 0.6, steep);
  float inFormation = smoothstep(2140.0, 2200.0, vWorld.y) * (1.0 - smoothstep(2600.0, 2660.0, vWorld.y)) * max(w1, w0);
  float sy = strataY(vWorld);
  vec4 st = strataAt(sy);
  // talus/regolith: eroded from the beds above -> blurred, paler strata color
  vec3 talus = strataAtLod(sy + 6.0, 6.0).rgb;
  talus = mix(talus, vec3(lum3(talus)), 0.12) * 1.08;
  float lnorm = clamp(la / 0.33, 0.55, 1.45);
  float forest = smoothstep(0.2, 0.6, veg);
  vec3 formCol = talus * lnorm;
  alb = mix(alb, formCol, inFormation * 0.7 * (1.0 - forest * 0.85));
  vec3 rockCol = mix(st.rgb, strataAtLod(sy, 5.0).rgb, 0.35) * mix(1.0, lnorm, 0.5);
  // vertical runnels / desert varnish streaks
  float detailW = (1.0 - smoothstep(80.0, 600.0, dist)) * uDetailFade;
  vec2 ruv = vec2((p.x + p.y) * 0.9, vWorld.y * 0.045);
  float rill = texture(uNoise, ruv).r;
  rockCol *= mix(1.0, 0.85 + 0.25 * rill, rockMask * 0.8);
  alb = mix(alb, rockCol, rockMask * inFormation);

  // --- near-field ground detail
  vec4 dn = texture(uNoise, p * 0.21);
  vec4 dn2 = texture(uNoise, p * 1.37 + 0.31);
  float var = mix(1.0, (0.86 + 0.28 * dn.r) * (0.9 + 0.2 * dn2.a), detailW);
  // pebbles: worley cells read as small stones on gentle slopes
  float peb = (1.0 - smoothstep(0.0, 0.35, dn2.g)) * (1.0 - rockMask) * detailW;
  alb *= var;
  alb = mix(alb, alb * (0.72 + 0.5 * dn.a), peb * 0.6);
  // forest floor on the plateau: darker brown duff where vegetation is dense
  // forest floor on the plateau: needle duff + pinkish soil instead of pale photo ground
  float plateauG = smoothstep(2395.0, 2430.0, vWorld.y) * (1.0 - rockMask);
  float duff = smoothstep(0.15, 0.6, veg) * plateauG;
  vec3 soil = vec3(0.30, 0.20, 0.14) * (0.85 + 0.3 * dn.r);
  vec3 needles = vec3(0.17, 0.11, 0.065) * (0.8 + 0.4 * dn2.g);
  alb = mix(alb, mix(alb * vec3(0.92, 0.82, 0.74), soil, 0.45), plateauG * 0.85);
  alb = mix(alb, needles, duff * 0.55);

  float rough = mix(0.92, 0.85, rockMask);

  // --- trails, paved overlook, roads (near level only)
  float dirt = 0.0, paved = 0.0, road = 0.0;
  if (w0 > 0.0) {
    vec4 tr = texture(uTrail, uv0);
    float jitter = (dn2.r - 0.5) * 0.35 + (dn.g - 0.5) * 0.2;
    float dDirt = tr.r * 12.0 + jitter;
    float dPaved = tr.g * 12.0 + jitter * 0.3;
    float dRoad = tr.b * 24.0;
    dirt = (1.0 - smoothstep(0.55, 0.9, dDirt)) * w0;
    float dirtEdge = (1.0 - smoothstep(0.9, 1.6, dDirt)) * w0;
    paved = (1.0 - smoothstep(0.95, 1.12, dPaved)) * w0;
    road = (1.0 - smoothstep(3.3, 3.55, dRoad)) * w0;
    vec3 trailCol = mix(vec3(0.56, 0.40, 0.29), alb * 1.25, 0.35) * (0.88 + 0.24 * dn2.g);
    alb = mix(alb, alb * 0.85, dirtEdge * (1.0 - dirt) * 0.6);
    alb = mix(alb, trailCol, dirt * 0.92);
    vec3 pavedCol = vec3(0.30, 0.28, 0.26) * (0.9 + 0.2 * dn2.a);
    alb = mix(alb, pavedCol, paved);
    vec3 asphalt = vec3(0.14, 0.13, 0.125) * (0.85 + 0.3 * dn2.a);
    alb = mix(alb, asphalt, road);
    rough = mix(rough, 0.75, max(paved, road));
    rockMask *= 1.0 - max(dirt, max(paved, road));
    N = normalize(mix(N, vec3(0.0, 1.0, 0.0), max(paved, road) * 0.5));
  }

  // --- Blender ground materials (close range)
  float gW = uGroundOn * (1.0 - smoothstep(30.0, 130.0, dist)) * w0;
  if (gW > 0.001) {
    float wRock = rockMask;
    float wTrail = dirt * (1.0 - wRock);
    float wDuff = duff * (1.0 - wRock) * (1.0 - wTrail);
    float wGrass = plateauG * (1.0 - duff) * (1.0 - wTrail) * (1.0 - wRock) * 0.8;
    float wReg = max(0.0, 1.0 - wRock - wTrail - wDuff - wGrass) * (1.0 - max(paved, road));
    vec3 det = vec3(0.0);
    vec3 tn = vec3(0.0);
    float wsum = 0.0;
    vec2 pr = abs(N.x) > abs(N.z) ? vec2(vWorld.z, vWorld.y) : vec2(vWorld.x, vWorld.y);
    if (wReg > 0.01) { det += groundDetail(p, 0.0) * wReg; tn += groundNormalTS(p, 0.0) * wReg; wsum += wReg; }
    if (wTrail > 0.01) { det += groundDetail(p, 1.0) * wTrail; tn += groundNormalTS(p, 1.0) * wTrail; wsum += wTrail; }
    if (wDuff > 0.01) { det += groundDetail(p, 2.0) * wDuff; tn += groundNormalTS(p, 2.0) * wDuff; wsum += wDuff; }
    if (wGrass > 0.01) { det += groundDetail(p, 5.0) * wGrass; tn += groundNormalTS(p, 5.0) * wGrass; wsum += wGrass; }
    if (wRock > 0.01) { det += groundDetail(pr, 3.0) * wRock; tn += groundNormalTS(pr, 3.0) * wRock; wsum += wRock; }
    if (wsum > 0.0) {
      det /= wsum; tn /= wsum;
      alb *= mix(vec3(1.0), det * 2.0, gW * 0.85);
      // world-aligned tangent frame: x / z on the ground, projection axes on rock
      vec3 T = abs(N.y) > 0.5 ? vec3(1.0, 0.0, 0.0) : (abs(N.x) > abs(N.z) ? vec3(0.0, 0.0, 1.0) : vec3(1.0, 0.0, 0.0));
      vec3 B = normalize(cross(N, T));
      T = cross(B, N);
      vec3 Nd = normalize(T * tn.x + B * tn.y + N * max(tn.z, 0.2));
      N = normalize(mix(N, Nd, gW));
    }
  }

  // --- micro normal detail
  if (detailW > 0.01) {
    N = triNoiseNormal(vWorld, N, mix(0.9, 0.35, rockMask), mix(0.05, 0.11, rockMask) * detailW * (1.0 - gW * 0.6));
    // vertical rain flutes on steep faces (stretched along y)
    if (rockMask > 0.05) {
      vec2 fuv = vec2((abs(N.x) > abs(N.z) ? vWorld.z : vWorld.x) * 0.55, vWorld.y * 0.06);
      float f0 = texture(uNoise, fuv).r;
      float f1 = texture(uNoise, fuv + vec2(0.02, 0.0)).r;
      vec3 side = normalize(cross(vec3(0.0, 1.0, 0.0), N) + 1e-4);
      N = normalize(N + side * (f1 - f0) * 9.0 * rockMask * detailW);
      alb *= mix(1.0, 0.86 + 0.28 * f0, rockMask * 0.6);
    }
  }

  // --- snow & wetness
  float snowSlope = N.y + (dn.r - 0.5) * 0.35 + (dn2.r - 0.5) * 0.12;
  float snowThr = mix(1.05, 0.35, uSnowCover);
  float snow = smoothstep(snowThr, snowThr + 0.12, snowSlope) * step(0.001, uSnowCover);
  snow *= 1.0 - 0.45 * smoothstep(0.4, 0.9, veg) * w0;
  vec3 snowCol = vec3(0.88, 0.9, 0.93) * (0.96 + 0.08 * dn2.a);
  if (gW > 0.001 && snow > 0.01) snowCol *= mix(vec3(1.0), groundDetail(p, 4.0) * 2.0, gW * 0.6);
  alb = mix(alb * mix(1.0, 0.6, uWetness), snowCol, snow);
  rough = mix(mix(rough, 0.32, uWetness * (1.0 - rockMask * 0.4)), 0.55, snow);

  vec3 V = normalize(cameraPosition - vWorld);
  float sh = getShadow(vWorld, N, gl_FragCoord.xy);
  vec3 bounce = vec3(0.55, 0.30, 0.18);
  float ao = mix(1.0, 0.75 + 0.25 * dn.r, detailW * rockMask);
  vec3 col = shadeSurface(alb, N, V, vWorld, rough, 0.035 + uWetness * 0.02, sh, skyVis, ao, bounce, 0.0);
  fragColor = vec4(col, 1.0);
}
`;

const depthFrag = /* glsl */ `
layout(location = 0) out vec4 fragColor;
void main() { fragColor = vec4(1.0); }
`;

interface PatchSet {
  geometry: THREE.InstancedBufferGeometry;
  attr: THREE.InstancedBufferAttribute;
  mesh: THREE.Mesh;
  scene: THREE.Scene;
  count: number;
}

export class Terrain implements ShadowCaster {
  static SHADOW_LOD_SCALE = [1, 1.6, 2.6, 4];
  readonly material: THREE.ShaderMaterial;
  readonly depthMaterial: THREE.ShaderMaterial;
  readonly main: PatchSet;
  private readonly shadowSets: PatchSet[] = [];
  private readonly grid: THREE.BufferGeometry;
  private readonly ranges: number[] = [];
  private readonly rootX0: number;
  private readonly rootZ0: number;
  private readonly frustum = new THREE.Frustum();
  private readonly box = new THREE.Box3();
  private readonly projView = new THREE.Matrix4();
  private readonly rangeCache = new Map<string, [number, number]>();
  readonly lodCam = new THREE.Vector3();
  nodesDrawn = 0;

  constructor(private readonly data: TerrainData, gridN: number, baseRange: number, trailTex: THREE.Texture) {
    // --- shared grid
    const n = gridN;
    const pos = new Float32Array((n + 1) * (n + 1) * 3);
    for (let z = 0, i = 0; z <= n; z++) for (let x = 0; x <= n; x++, i += 3) {
      pos[i] = x / n; pos[i + 1] = 0; pos[i + 2] = z / n;
    }
    const idx: number[] = [];
    for (let z = 0; z < n; z++) for (let x = 0; x < n; x++) {
      const a = z * (n + 1) + x, b = a + 1, c = a + n + 1, d = c + 1;
      // alternate diagonals for symmetric morphing
      if ((x + z) % 2 === 0) idx.push(a, c, b, b, c, d);
      else idx.push(a, c, d, a, d, b);
    }
    this.grid = new THREE.BufferGeometry();
    this.grid.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    this.grid.setIndex(idx);

    for (let L = 0; L <= MAX_LEVEL; L++) this.ranges.push(baseRange * Math.pow(2, L));
    const morph: THREE.Vector2[] = [];
    for (let L = 0; L <= MAX_LEVEL; L++) {
      const lo = L === 0 ? 0 : this.ranges[L - 1];
      const hi = this.ranges[L];
      const start = lo + (hi - lo) * 0.55;
      const end = hi * 0.96;
      morph.push(new THREE.Vector2(start, 1 / (end - start)));
    }

    const far = data.levels[data.levels.length - 1];
    const rootSize = LEAF * Math.pow(2, MAX_LEVEL);
    this.rootX0 = far.x0 + far.size / 2 - rootSize / 2;
    this.rootZ0 = far.z0 + far.size / 2 - rootSize / 2;

    const [L0, L1, L2] = data.levels;
    const hb = (L: typeof L0) => new THREE.Vector4(L.x0, L.z0, L.size, L.res);
    const terrainUniforms = {
      uH0: { value: L0.heightTex }, uH1: { value: L1.heightTex }, uH2: { value: L2.heightTex },
      uHB0: { value: hb(L0) }, uHB1: { value: hb(L1) }, uHB2: { value: hb(L2) },
      uHM: { value: new THREE.Vector3(L0.margin, L1.margin, 0) },
      uMorph: { value: morph },
      uGridN: { value: n },
      uLodCam: { value: this.lodCam },
      uLodScale: { value: 1 },
    };
    this.material = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: commonGLSL + curvatureGLSL + terrainHeightGLSL + terrainVert,
      fragmentShader: commonGLSL + terrainHeightGLSL + lightingGLSL + shadowGLSL + strataGLSL + groundGLSL + terrainFrag,
      uniforms: {
        ...shared,
        ...terrainUniforms,
        uN0: { value: L0.normalTex }, uN1: { value: L1.normalTex }, uN2: { value: L2.normalTex },
        uC0: { value: L0.albedoTex }, uC1: { value: L1.albedoTex }, uC2: { value: L2.albedoTex },
        uTrail: { value: trailTex },
        uGroundAlb: { value: null },
        uGroundNrm: { value: null },
        uGroundOn: { value: 0 },
        uGroundTile: { value: 2 },
      },
    });
    this.depthMaterial = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: commonGLSL + curvatureGLSL + terrainHeightGLSL + terrainVert,
      fragmentShader: depthFrag,
      uniforms: { ...shared, ...terrainUniforms, uLodScale: { value: 1 } },
    });

    this.main = this.makeSet(this.material);
    for (let i = 0; i < 4; i++) this.shadowSets.push(this.makeSet(this.depthMaterial));
  }

  setGround(g: GroundSet | null) {
    const u = this.material.uniforms;
    if (!g) { u.uGroundOn.value = 0; return; }
    u.uGroundAlb.value = g.albedo;
    u.uGroundNrm.value = g.normal;
    u.uGroundTile.value = g.tileMeters;
    u.uGroundOn.value = 1;
  }

  /** Attach shadow uniforms (owned by the Shadows system) to the lit material. */
  bindShadowUniforms(u: Record<string, THREE.IUniform>) {
    Object.assign(this.material.uniforms, u);
  }

  private makeSet(mat: THREE.ShaderMaterial): PatchSet {
    const geometry = new THREE.InstancedBufferGeometry();
    geometry.index = this.grid.index;
    geometry.setAttribute('position', this.grid.getAttribute('position'));
    const attr = new THREE.InstancedBufferAttribute(new Float32Array(MAX_NODES * 4), 4);
    attr.setUsage(THREE.DynamicDrawUsage);
    geometry.setAttribute('aNode', attr);
    geometry.instanceCount = 0;
    const mesh = new THREE.Mesh(geometry, mat);
    mesh.frustumCulled = false;
    const scene = new THREE.Scene();
    scene.add(mesh);
    return { geometry, attr, mesh, scene, count: 0 };
  }

  private nodeRange(L: number, x0: number, z0: number, size: number): [number, number] {
    const key = `${L}_${x0}_${z0}`;
    let r = this.rangeCache.get(key);
    if (!r) {
      r = this.data.rangeAt(x0, z0, size);
      r = [r[0] - 2, r[1] + 2];
      if (this.rangeCache.size > 60000) this.rangeCache.clear();
      this.rangeCache.set(key, r);
    }
    return r;
  }

  private select(set: PatchSet, camera: THREE.Camera, lodPos: THREE.Vector3, lodScale = 1, minDist = 0) {
    this.projView.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    this.frustum.setFromProjectionMatrix(this.projView);
    set.count = 0;
    const arr = set.attr.array as Float32Array;
    const visit = (L: number, x0: number, z0: number) => {
      const size = LEAF * Math.pow(2, L);
      const [ymin, ymax] = this.nodeRange(L, x0, z0, size);
      // curvature: distant nodes sit lower; extend the box downward
      const cx = x0 + size / 2 - lodPos.x, cz = z0 + size / 2 - lodPos.z;
      const drop = Math.max(0, (Math.hypot(cx, cz) - size) ** 2) / (2 * 6371000);
      this.box.min.set(x0, ymin - drop - 1, z0);
      this.box.max.set(x0 + size, ymax + 1, z0 + size);
      if (!this.frustum.intersectsBox(this.box)) return;
      if (minDist > 0) {
        // skip nodes lying entirely closer than minDist (they belong to finer cascades)
        const fx = Math.max(Math.abs(x0 - lodPos.x), Math.abs(x0 + size - lodPos.x));
        const fz = Math.max(Math.abs(z0 - lodPos.z), Math.abs(z0 + size - lodPos.z));
        if (Math.hypot(fx, fz) < minDist) return;
      }
      if (L === 0) { push(x0, z0, size, 0); return; }
      const d = this.box.distanceToPoint(lodPos) * lodScale;
      if (d > this.ranges[L - 1]) { push(x0, z0, size, L); return; }
      const h = size / 2;
      visit(L - 1, x0, z0); visit(L - 1, x0 + h, z0); visit(L - 1, x0, z0 + h); visit(L - 1, x0 + h, z0 + h);
    };
    const push = (x0: number, z0: number, size: number, L: number) => {
      if (set.count >= MAX_NODES) return;
      const i = set.count * 4;
      arr[i] = x0; arr[i + 1] = z0; arr[i + 2] = size; arr[i + 3] = L;
      set.count++;
    };
    visit(MAX_LEVEL, this.rootX0, this.rootZ0);
    set.geometry.instanceCount = set.count;
    set.attr.addUpdateRange(0, set.count * 4);
    set.attr.needsUpdate = true;
  }

  update(camera: THREE.PerspectiveCamera) {
    this.lodCam.copy(camera.position);
    this.select(this.main, camera, this.lodCam);
    this.nodesDrawn = this.main.count;
  }

  get mesh(): THREE.Mesh {
    return this.main.mesh;
  }

  renderShadow(renderer: THREE.WebGLRenderer, cam: THREE.OrthographicCamera, cascade: number, nearDist: number) {
    const set = this.shadowSets[cascade];
    const scale = Terrain.SHADOW_LOD_SCALE[cascade] ?? 4;
    this.depthMaterial.uniforms.uLodScale.value = scale;
    this.select(set, cam, this.lodCam, scale, nearDist);
    renderer.render(set.scene, cam);
  }
}
